import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  Grid,
  Box,
  Button,
  IconButton,
  TextField,
  Typography,
  Dialog,
  DialogContent,
  DialogActions,
  MenuItem,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
  Divider,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { TrashIcon, PlusIcon, PenIcon, Logo } from "../components/svgicons/SvgIcons";
import mrpintoresLogo from "../resources/images/mrpintores-logo.png";
import FancyPaper from "../components/FancyPaper";
import FancyTooltip from "../components/utils/FancyTooltip";
import { useApp } from "../hooks/useApp";
import {
  getStaffListsByProject,
  createStaffList,
  updateStaffList,
  deleteStaffList,
  getMembersByStaffList,
  createStaffListMember,
  deleteStaffListMember,
  getProjectSender,
  saveProjectSender,
  getProjectVehicle,
  saveProjectVehicle,
  getProjectSignature,
  saveProjectSignature,
  seedProjectDefaultData,
  downloadStaffListPdf,
  downloadStaffListWord,
} from "../services/projects/StaffListService";
import {
  getRequestPerson,
  getRejectedPersonIdsByProject,
} from "../services/person/PersonService";
import { getProjectById } from "../services/projects/ProjectService";
import { getRequestPosition } from "../services/position/PositionService";
import DeleteModal from "../components/utils/DeleteModal";
import FancyDialogActions from "../components/utils/FancyDialogActions";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import "dayjs/locale/es";

// ─── Types ───────────────────────────────────────────────────────────────────

interface FormData {
  contractName: string;
  startDate: string;
  startTime: string;
  endTime: string;
  days: string[]; // array of day keys: "monday","tuesday", etc.
}

interface FormMember {
  id?: number; // undefined = new, number = existing
  personId: number;
  personName: string;
  personDni: string;
  positionId: number;
  positionName: string;
}

/**
 * Estados en los que un colaborador puede integrar una lista de personal:
 * Activo (0) y Periodo de Prueba (2). Inactivo, Liquidado y Lista Negra
 * quedan fuera del buscador.
 */
const SELECTABLE_PERSON_STATES = ["0", "2"];

const buildPersonFullName = (person: any) =>
  `${person?.name ?? ""} ${person?.lastName ?? ""} ${person?.surname ?? ""}`.trim();

const EMPTY_FORM: FormData = {
  contractName: "",
  startDate: "",
  startTime: "",
  endTime: "",
  days: [],
};

const DAY_LABELS: { key: string; label: string }[] = [
  { key: "monday", label: "L" },
  { key: "tuesday", label: "M" },
  { key: "wednesday", label: "X" },
  { key: "thursday", label: "J" },
  { key: "friday", label: "V" },
  { key: "saturday", label: "S" },
  { key: "sunday", label: "D" },
];

function buildDaysBooleans(days: string[]) {
  return {
    monday: days.includes("monday"),
    tuesday: days.includes("tuesday"),
    wednesday: days.includes("wednesday"),
    thursday: days.includes("thursday"),
    friday: days.includes("friday"),
    saturday: days.includes("saturday"),
    sunday: days.includes("sunday"),
  };
}

function daysFromBooleans(item: any): string[] {
  const d: string[] = [];
  if (item.monday) d.push("monday");
  if (item.tuesday) d.push("tuesday");
  if (item.wednesday) d.push("wednesday");
  if (item.thursday) d.push("thursday");
  if (item.friday) d.push("friday");
  if (item.saturday) d.push("saturday");
  if (item.sunday) d.push("sunday");
  return d;
}

function toTimeString(value: any): string {
  if (!value) return "";
  if (Array.isArray(value)) {
    const h = String(value[0] ?? 0).padStart(2, "0");
    const m = String(value[1] ?? 0).padStart(2, "0");
    return `${h}:${m}`;
  }
  return String(value).substring(0, 5);
}

function format12h(timeStr: string): string {
  if (!timeStr) return "";
  let [hours, minutes] = timeStr.split(":").map(Number);
  const ampm = hours >= 12 ? "pm" : "am";
  hours = hours % 12;
  hours = hours ? hours : 12; // el 0 es 12
  return `${hours}:${String(minutes).padStart(2, "0")}${ampm}`;
}

const SPANISH_DAYS: { [key: string]: string } = {
  monday: "lunes",
  tuesday: "martes",
  wednesday: "miércoles",
  thursday: "jueves",
  friday: "viernes",
  saturday: "sábado",
  sunday: "domingo",
};

const MONTHS = [
  "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"
];

function formatFullSchedule(item: any): string {
  if (!item.startDate) return "";
  
  // Fecha
  let datePart = "";
  if (Array.isArray(item.startDate)) {
    datePart = `${item.startDate[2]} de ${MONTHS[item.startDate[1] - 1]} de ${item.startDate[0]}`;
  } else {
    const d = new Date(item.startDate + "T00:00:00");
    datePart = `${d.getDate()} de ${MONTHS[d.getMonth()]} de ${d.getFullYear()}`;
  }

  // Horario
  const start = format12h(toTimeString(item.startTime));
  const end = format12h(toTimeString(item.endTime));
  
  // Días
  const selectedDays = DAY_LABELS.filter(d => item[d.key]).map(d => SPANISH_DAYS[d.key]);
  let daysStr = "";
  if (selectedDays.length > 0) {
    if (selectedDays.length === 1) daysStr = `el ${selectedDays[0]}`;
    else if (selectedDays.length === 7) daysStr = "de lunes a domingo";
    else daysStr = `de ${selectedDays[0]} a ${selectedDays[selectedDays.length - 1]}`;
  }

  return `A partir del ${datePart}, en el siguiente horario de ${start} a ${end} ${daysStr}`;
}

function toDateString(value: any): string {
  if (!value) return "";
  if (Array.isArray(value)) {
    const y = value[0];
    const mo = String(value[1] ?? 1).padStart(2, "0");
    const d = String(value[2] ?? 1).padStart(2, "0");
    return `${y}-${mo}-${d}`;
  }
  return String(value).substring(0, 10);
}

function formatSchedule(item: any) {
  const days = DAY_LABELS.filter((d) => item[d.key])
    .map((d) => d.label)
    .join(" ");
  const start = toTimeString(item.startTime);
  const end = toTimeString(item.endTime);
  return `${days} | ${start} - ${end}`;
}

// ─── Component ───────────────────────────────────────────────────────────────

const ManagementStaffList = () => {
  const { projectId, projectName } =
    useParams<{ projectId: string; projectName: string }>();
  const { setLoading, setErrorMsg, setSuccessMsg, authInfo } = useApp();

  const numericProjectId = Number(projectId);
  const decodedProjectName = decodeURIComponent(projectName ?? "");

  // ── Data ──────────────────────────────────────────────────────────────────
  const [staffLists, setStaffLists] = useState<any[]>([]);
  const [membersMap, setMembersMap] = useState<{ [id: number]: FormMember[] }>(
    {}
  );
  const [sender, setSender] = useState<any | null>(null);
  const [vehicle, setVehicle] = useState<any | null>(null);
  const [signature, setSignature] = useState<any | null>(null);
  const [positions, setPositions] = useState<any[]>([]);

  // ── Form ──────────────────────────────────────────────────────────────────
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingListId, setEditingListId] = useState<number | null>(null);
  const [formData, setFormData] = useState<FormData>({ ...EMPTY_FORM });
  const [formMembers, setFormMembers] = useState<FormMember[]>([]);
  const [originalMembers, setOriginalMembers] = useState<FormMember[]>([]);

  // Person search
  const [personQuery, setPersonQuery] = useState("");
  const [personResults, setPersonResults] = useState<any[]>([]);
  const [selectedPerson, setSelectedPerson] = useState<any | null>(null);
  const [selectedPosition, setSelectedPosition] = useState<any | null>(null);

  // Side-info edit
  const [editSender, setEditSender] = useState(false);
  const [editVehicle, setEditVehicle] = useState(false);
  const [editSignature, setEditSignature] = useState(false);
  const [senderContent, setSenderContent] = useState("");
  const [vehicleContent, setVehicleContent] = useState("");
  const [signatureContent, setSignatureContent] = useState("");

  // Modals
  const [downloadListId, setDownloadListId] = useState<number | null>(null);
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [deleteListId, setDeleteListId] = useState<number | null>(null);

  // Permiso especial de personal: colaboradores con el ingreso rechazado a
  // este proyecto. Vacío cuando el proyecto no exige permiso especial.
  const [rejectedPersonIds, setRejectedPersonIds] = useState<Set<number>>(
    new Set()
  );

  // ── Init ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    const init = async () => {
      await loadAll();
      await loadRejectedPersonIds();
    };
    init();
    // eslint-disable-next-line
  }, []);

  const loadRejectedPersonIds = async () => {
    try {
      const project = await getProjectById(numericProjectId);
      if (!project?.specialPermitRequired) {
        setRejectedPersonIds(new Set());
        return;
      }
      const ids = await getRejectedPersonIdsByProject(numericProjectId);
      setRejectedPersonIds(new Set(ids));
    } catch {
      setRejectedPersonIds(new Set());
    }
  };

  const loadAll = async () => {
    try {
      setLoading(true);
      const [lists, senderData, vehicleData, signatureData, posData] =
        await Promise.allSettled([
          getStaffListsByProject(numericProjectId),
          getProjectSender(numericProjectId),
          getProjectVehicle(numericProjectId),
          getProjectSignature(numericProjectId),
          getRequestPosition(0, "", 500),
        ]);

      if (lists.status === "fulfilled") {
        const listsData = lists.value ?? [];
        setStaffLists(listsData);
        if (listsData.length > 0) {
          const membersResults = await Promise.allSettled(
            listsData.map((l: any) => getMembersByStaffList(l.id))
          );
          const newMap: { [id: number]: FormMember[] } = {};
          listsData.forEach((l: any, i: number) => {
            const r = membersResults[i];
            if (r.status === "fulfilled") {
              newMap[l.id] = (r.value ?? []).map((m: any) => ({
                id: m.id,
                personId: m.personId,
                personName: m.personName ?? "",
                personDni: m.personDni ?? "",
                positionId: m.positionId,
                positionName: m.positionName ?? "",
              }));
            }
          });
          setMembersMap(newMap);
        }
      }

      // Si no existe el sender, inicializar datos por defecto del proyecto
      const needsSeed =
        (senderData.status === "fulfilled" && !senderData.value) ||
        (senderData.status === "rejected" &&
          senderData.reason?.response?.status === 404);
      if (needsSeed) {
        await seedProjectDefaultData(numericProjectId);
        // Recargar los tres registros recién creados
        const [newSender, newVehicle, newSignature] = await Promise.all([
          getProjectSender(numericProjectId),
          getProjectVehicle(numericProjectId),
          getProjectSignature(numericProjectId),
        ]);
        setSender(newSender);
        setSenderContent(newSender?.content ?? "");
        setVehicle(newVehicle);
        setVehicleContent(newVehicle?.content ?? "");
        setSignature(newSignature);
        setSignatureContent(newSignature?.content ?? "");
      } else {
        if (senderData.status === "fulfilled" && senderData.value) {
          setSender(senderData.value);
          setSenderContent(senderData.value.content ?? "");
        }
        if (vehicleData.status === "fulfilled" && vehicleData.value) {
          setVehicle(vehicleData.value);
          setVehicleContent(vehicleData.value.content ?? "");
        }
        if (signatureData.status === "fulfilled" && signatureData.value) {
          setSignature(signatureData.value);
          setSignatureContent(signatureData.value.content ?? "");
        }
      }

      if (posData.status === "fulfilled") {
        setPositions(posData.value?.content ?? []);
      }
    } catch (err: any) {
      setErrorMsg(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadMembers = useCallback(
    async (staffListId: number) => {
      try {
        const data = await getMembersByStaffList(staffListId);
        const members: FormMember[] = (data ?? []).map((m: any) => ({
          id: m.id,
          personId: m.personId,
          personName: m.personName ?? "",
          personDni: m.personDni ?? "",
          positionId: m.positionId,
          positionName: m.positionName ?? "",
        }));
        setMembersMap((prev) => ({ ...prev, [staffListId]: members }));
        return members;
      } catch {
        return [];
      }
    },
    // eslint-disable-next-line
    []
  );

  // ── Person search ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (personQuery.length < 1) {
      setPersonResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        // El endpoint filtra por un único estado, así que se consulta cada uno
        // de los admitidos por separado y se unifica el resultado.
        const pages = await Promise.all(
          SELECTABLE_PERSON_STATES.map((state) =>
            getRequestPerson(0, personQuery, state, 20)
          )
        );
        const byId = new Map<number, any>();
        pages.forEach((page: any) =>
          (page?.content ?? []).forEach((person: any) =>
            byId.set(person.id, person)
          )
        );
        setPersonResults(
          Array.from(byId.values()).sort((a, b) =>
            buildPersonFullName(a).localeCompare(buildPersonFullName(b), "es")
          )
        );
      } catch {
        setPersonResults([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [personQuery]);

  // ── Form helpers ──────────────────────────────────────────────────────────
  const openAddForm = () => {
    const last =
      staffLists.length > 0 ? staffLists[staffLists.length - 1] : null;
    if (last) {
      setFormData({
        contractName: last.contractName ?? "",
        startDate: toDateString(last.startDate),
        startTime: toTimeString(last.startTime),
        endTime: toTimeString(last.endTime),
        days: daysFromBooleans(last),
      });
    } else {
      setFormData({ ...EMPTY_FORM });
    }
    // Las personas no se precargan de la última lista: cada lista se arma desde cero.
    setFormMembers([]);
    setOriginalMembers([]);
    setEditingListId(null);
    setSelectedPerson(null);
    setSelectedPosition(null);
    setPersonQuery("");
    setShowAddForm(true);
  };

  const openEditForm = async (list: any) => {
    const days = daysFromBooleans(list);
    setFormData({
      contractName: list.contractName ?? "",
      startDate: toDateString(list.startDate),
      startTime: toTimeString(list.startTime),
      endTime: toTimeString(list.endTime),
      days,
    });
    const members = await loadMembers(list.id);
    setFormMembers([...members]);
    setOriginalMembers([...members]);
    setSelectedPerson(null);
    setSelectedPosition(null);
    setPersonQuery("");
    setEditingListId(list.id);
    setShowAddForm(false);
  };

  const cancelForm = () => {
    setShowAddForm(false);
    setEditingListId(null);
    setFormData({ ...EMPTY_FORM });
    setFormMembers([]);
    setOriginalMembers([]);
    setSelectedPerson(null);
    setSelectedPosition(null);
  };

  const handleDaysChange = (
    _: React.MouseEvent<HTMLElement>,
    newDays: string[]
  ) => {
    setFormData((f) => ({ ...f, days: newDays }));
  };

  const handleAddMember = () => {
    if (!selectedPerson || !selectedPosition) return;
    const pos = selectedPosition;

    const fullName = `${selectedPerson.name ?? ""} ${selectedPerson.lastName ?? ""} ${selectedPerson.surname ?? ""}`.trim();

    if (rejectedPersonIds.has(selectedPerson.id)) {
      setErrorMsg(
        `${fullName || "Este colaborador"} tiene el ingreso rechazado a este proyecto y no puede agregarse a la lista de personal.`
      );
      return;
    }

    const already = formMembers.find(
      (m) => m.personId === selectedPerson.id && m.positionId === pos.id
    );
    if (already) {
      setErrorMsg("Esta persona con ese cargo ya fue agregada.");
      return;
    }

    setFormMembers((prev) => [
      ...prev,
      {
        personId: selectedPerson.id,
        personName: fullName,
        personDni: selectedPerson.dni ?? "",
        positionId: pos.id,
        positionName: pos.name,
      },
    ]);
    setSelectedPerson(null);
    setSelectedPosition(null);
    setPersonQuery("");
  };

  const handleRemoveFormMember = (idx: number) => {
    setFormMembers((prev) => prev.filter((_, i) => i !== idx));
  };

  // ── Save list ─────────────────────────────────────────────────────────────
  const handleSaveList = async () => {
    try {
      setLoading(true);
      const payload = {
        user: authInfo?.username,
        projectId: numericProjectId,
        contractName: formData.contractName,
        startDate: formData.startDate,
        startTime: formData.startTime ? formData.startTime + ":00" : null,
        endTime: formData.endTime ? formData.endTime + ":00" : null,
        ...buildDaysBooleans(formData.days),
      };

      if (editingListId === null) {
        // Create
        const created = await createStaffList(payload);
        const newId: number = created.id;
        for (const m of formMembers) {
          await createStaffListMember({
              user: authInfo?.username,
              staffListId: newId,
            personId: m.personId,
            positionId: m.positionId,
          });
        }
      } else {
        // Update
        await updateStaffList({ ...payload, id: editingListId });

        // Add new members (those without id)
        for (const m of formMembers) {
          if (!m.id) {
            await createStaffListMember({
              user: authInfo?.username,
              staffListId: editingListId,
              personId: m.personId,
              positionId: m.positionId,
            });
          }
        }

        // Delete removed members (those in original but not in current)
        for (const orig of originalMembers) {
          const stillPresent = formMembers.find((m) => m.id === orig.id);
          if (!stillPresent && orig.id) {
            await deleteStaffListMember(orig.id);
          }
        }
      }

      setSuccessMsg && setSuccessMsg("Lista guardada correctamente.");
      cancelForm();
      await loadAll();
    } catch (err: any) {
      setErrorMsg(err.message ?? "Error al guardar lista.");
    } finally {
      setLoading(false);
    }
  };

  // ── Delete list ───────────────────────────────────────────────────────────
  const handleConfirmDelete = async () => {
    if (!deleteListId) return;
    try {
      setLoading(true);
      await deleteStaffList(deleteListId);
      setSuccessMsg && setSuccessMsg("Lista eliminada correctamente.");
      setDeleteListId(null);
      await loadAll();
    } catch (err: any) {
      setErrorMsg(err.message ?? "Error al eliminar lista.");
    } finally {
      setLoading(false);
    }
  };

  // ── Save side info ────────────────────────────────────────────────────────
  const handleSaveSender = async () => {
    try {
      setLoading(true);
      const saved = await saveProjectSender({
        ...(sender ? { id: sender.id } : {}),
        projectId: numericProjectId,
        content: senderContent,
      });
      setSender(saved);
      setEditSender(false);
      setSuccessMsg && setSuccessMsg("Remitente guardado.");
    } catch (err: any) {
      setErrorMsg(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveVehicle = async () => {
    try {
      setLoading(true);
      const saved = await saveProjectVehicle({
        ...(vehicle ? { id: vehicle.id } : {}),
        projectId: numericProjectId,
        content: vehicleContent,
      });
      setVehicle(saved);
      setEditVehicle(false);
      setSuccessMsg && setSuccessMsg("Vehículos guardados.");
    } catch (err: any) {
      setErrorMsg(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSignature = async () => {
    try {
      setLoading(true);
      const saved = await saveProjectSignature({
        ...(signature ? { id: signature.id } : {}),
        projectId: numericProjectId,
        content: signatureContent,
      });
      setSignature(saved);
      setEditSignature(false);
      setSuccessMsg && setSuccessMsg("Firmas guardadas.");
    } catch (err: any) {
      setErrorMsg(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ── Download ──────────────────────────────────────────────────────────────
  const handleOpenDownloadPreview = async (id: number) => {
    try {
      setLoading(true);
      // We need getStaffListPdfBlob or similar, let's just use the current service logic but get the URL
      const response = await downloadStaffListPdf(id, true); // We'll modify the service to return blob instead of download if requested
      if (response instanceof Blob) {
        const url = window.URL.createObjectURL(response);
        setPdfBlobUrl(url);
        setDownloadListId(id);
      }
    } catch (err: any) {
      setErrorMsg(err.message ?? "Error al generar previsualización.");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!downloadListId || !pdfBlobUrl) return;
    const link = document.createElement("a");
    link.href = pdfBlobUrl;
    link.download = `lista_personal_${downloadListId}.pdf`;
    link.click();
  };

  const handleDownloadWord = async () => {
    if (!downloadListId) return;
    try {
      setLoading(true);
      await downloadStaffListWord(downloadListId);
    } catch (err: any) {
      setErrorMsg(err.message ?? "Error al descargar Word.");
    } finally {
      setLoading(false);
    }
  };



  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      bgcolor: "#fff",
      overflow: "hidden",
      "& .MuiOutlinedInput-notchedOutline": {
        borderWidth: "1px",
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderWidth: "1px",
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderWidth: "1px",
      },
      "& fieldset": {
        borderRadius: "8px",
      },
    },
  };

  // ── Inline form ───────────────────────────────────────────────────────────
  const renderForm = (isEdit: boolean) => (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "rgba(0,0,0,0.1)",
        borderRadius: "16px",
        p: 3,
        mt: 2,
        bgcolor: "#E9E1DD",
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: '#333' }}>
        {isEdit ? 'Editar lista' : 'Agregar lista'}
      </Typography>

      <Grid container spacing={1} sx={{ mb: 4 }} alignItems="flex-end">
        {/* Days */}
        <Grid item xs={12} md={3}>
          <Typography variant="caption" sx={{ mb: 1, fontWeight: 600, color: '#666', display: 'block' }}>
            Días
          </Typography>
          <ToggleButtonGroup
            value={formData.days}
            onChange={handleDaysChange}
            size="small"
            sx={{ 
              display: "flex",
              width: "fit-content",
              justifyContent: "flex-start",
              flexWrap: "nowrap",
              gap: 0.6,
              "& .MuiToggleButtonGroup-grouped": {
                margin: 0,
              },
              "& .MuiToggleButton-root": {
                border: "1px solid #ccc !important",
                borderRadius: "8px !important",
                minWidth: "36px",
                height: "36px",
                bgcolor: "#fff",
                color: "#333",
                fontWeight: 600,
                "&.Mui-selected": {
                  bgcolor: "#d32f2f !important",
                  color: "#fff !important",
                }
              }
            }}
          >
            {DAY_LABELS.map((d) => (
              <ToggleButton key={d.key} value={d.key}>
                {d.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Grid>

        <Grid item xs={12} sm={6} md={2.5}>
          <TextField
            label="Hora inicio"
            type="time"
            value={formData.startTime}
            onChange={(e) =>
              setFormData((f) => ({ ...f, startTime: e.target.value }))
            }
            InputLabelProps={{ shrink: true }}
            fullWidth
            size="small"
            variant="outlined"
            sx={inputSx}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.5}>
          <TextField
            label="Hora fin"
            type="time"
            value={formData.endTime}
            onChange={(e) =>
              setFormData((f) => ({ ...f, endTime: e.target.value }))
            }
            InputLabelProps={{ shrink: true }}
            fullWidth
            size="small"
            variant="outlined"
            sx={inputSx}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={4}>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
            <DatePicker
              label="Fecha de ingreso"
              format="DD/MM/YYYY"
              value={formData.startDate ? dayjs(formData.startDate) : null}
              onChange={(date) => {
                setFormData((f) => ({
                  ...f,
                  startDate:
                    date && dayjs(date).isValid()
                      ? dayjs(date).format("YYYY-MM-DD")
                      : "",
                }));
              }}
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: "small",
                  variant: "outlined",
                  InputLabelProps: { shrink: true },
                  sx: inputSx,
                },
              }}
            />
          </LocalizationProvider>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 6 }}>
        <Grid item xs={12}>
          <TextField
            label="Nombre de contratación"
            placeholder="Escribir el nombre de contratación"
            value={formData.contractName}
            // Se muestra en 5 renglones pero el valor es de una sola línea: va
            // dentro de una frase del Word/PDF y un salto de línea la cortaría.
            onChange={(e) =>
              setFormData((f) => ({
                ...f,
                contractName: e.target.value.replace(/\r?\n/g, " "),
              }))
            }
            onKeyDown={(e) => {
              if (e.key === "Enter") e.preventDefault();
            }}
            fullWidth
            multiline
            minRows={5}
            size="small"
            variant="outlined"
            InputLabelProps={{ shrink: true }}
            inputProps={{ maxLength: 500 }}
            sx={inputSx}
          />
        </Grid>
      </Grid>

      {/* <Typography variant="caption" sx={{ fontWeight: 600, mb: 1, color: '#666', display: 'block' }}>
        Agregar personal
      </Typography> */}
      <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4} md={4}>
          <Autocomplete
            options={personResults}
            getOptionLabel={(opt: any) => buildPersonFullName(opt)}
            noOptionsText={
              personQuery.length < 1
                ? "Empezá a escribir para buscar"
                : "Sin resultados"
            }
            value={selectedPerson}
            onInputChange={(_, value) => setPersonQuery(value)}
            onChange={(_, value) => {
              setSelectedPerson(value);
              if (value?.positions && value.positions.length > 0) {
                setSelectedPosition(value.positions[0]);
              } else {
                setSelectedPosition(null);
              }
            }}
            isOptionEqualToValue={(opt, val) => opt.id === val?.id}
            renderInput={(params) => {
              const inputProps = params.inputProps;
              inputProps.autoComplete = "new-password";
              return (
                <TextField
                  {...params}
                  fullWidth
                  label="Buscar por nombre"
                  variant="outlined"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  sx={inputSx}
                  onKeyDown={(e) => {
                    if (
                      e.key === "Enter" &&
                      selectedPerson &&
                      selectedPosition
                    ) {
                      e.preventDefault();
                      e.stopPropagation();
                      handleAddMember();
                    }
                  }}
                />
              );
            }}
          />
        </Grid>
        <Grid item xs={12} sm={4} md={4}>
          <Autocomplete
            onChange={(_, newValue: any) => {
              setSelectedPosition(newValue);
            }}
            options={selectedPerson?.positions ?? []}
            getOptionLabel={(option: any) => option.name || ""}
            isOptionEqualToValue={(option: any, value: any) =>
              option.id === value.id
            }
            disabled={!selectedPerson}
            value={selectedPosition}
            noOptionsText="(Sin Cargos asignados)"
            renderInput={(params) => {
              const inputProps = params.inputProps;
              inputProps.autoComplete = "new-password";
              const noPositions =
                !!selectedPerson &&
                (selectedPerson.positions ?? []).length === 0;
              return (
                <TextField
                  {...params}
                  fullWidth
                  label="Cargo"
                  placeholder={
                    noPositions ? "(Sin Cargos asignados)" : undefined
                  }
                  variant="outlined"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  sx={inputSx}
                  onKeyDown={(e) => {
                    if (
                      e.key === "Enter" &&
                      selectedPerson &&
                      selectedPosition
                    ) {
                      e.preventDefault();
                      handleAddMember();
                    }
                  }}
                />
              );
            }}
          />
        </Grid>
        <Grid item xs={12} sm={3} md={2}>
          <Button
            variant="outlined"
            onClick={handleAddMember}
            disabled={!selectedPerson || !selectedPosition}
            fullWidth
            size="small"
            sx={{ 
              borderRadius: '8px', 
              textTransform: 'none', 
              height: '40px',
              borderColor: '#d32f2f',
              color: '#d32f2f',
              bgcolor: '#fff',
              fontWeight: 600,
              '&:hover': { borderColor: '#b71c1c', bgcolor: 'rgba(211, 47, 47, 0.04)' }
            }}
          >
            Agregar
          </Button>
        </Grid>
      </Grid>

      {/* Members table */}
      <TableContainer sx={{ mb: 3 }}>
        <Table size="small" sx={{ minWidth: 400 }}>
          <TableHead>
            <TableRow>
              <TableCell>Nombre</TableCell>
              <TableCell>Cargo</TableCell>
              <TableCell>Identificación</TableCell>
              <TableCell align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {formMembers.length === 0 ? (
               <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4, color: '#999', fontStyle: 'italic' }}>
                  No hay personal agregado a esta lista
                </TableCell>
               </TableRow>
            ) : (
              formMembers.map((m, idx) => (
                <TableRow key={idx}>
                  <TableCell>{m.personName}</TableCell>
                  <TableCell>{m.positionName}</TableCell>
                  <TableCell>{m.personDni}</TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleRemoveFormMember(idx)}
                    >
                      <TrashIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Actions */}
      <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end", mt: 2 }}>
        <Button 
          variant="outlined" 
          onClick={cancelForm}
          sx={{ 
            borderRadius: '20px', 
            textTransform: 'none', 
            px: 4, 
            borderColor: '#d32f2f', 
            color: '#d32f2f',
            fontWeight: 600
          }}
        >
          Cancelar
        </Button>
        <Button 
          variant="contained" 
          color="error" 
          onClick={handleSaveList}
          sx={{ 
            borderRadius: '20px', 
            textTransform: 'none', 
            px: 4, 
            bgcolor: '#d32f2f',
            fontWeight: 600,
            '&:hover': { bgcolor: '#b71c1c' }
          }}
        >
          Guardar lista
        </Button>
      </Box>
    </Box>
  );

  // ── Staff list card ───────────────────────────────────────────────────────
  const renderListCard = (list: any, index: number) => {
    const isEditing = editingListId === list.id;
    const members = membersMap[list.id] ?? [];
    const contractName = (list.contractName ?? "").trim();

    return (
      <Box
        key={list.id}
        sx={{
          border: "1px solid",
          borderColor: "rgba(0, 0, 0, 0.12)",
          borderRadius: "16px",
          p: 2,
          mb: 2,
          bgcolor: "#E9E1DD",
          boxShadow: "0px 2px 4px rgba(0,0,0,0.05)",
        }}
      >
        {!isEditing ? (
          <>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ color: "#333" }}>
                  Lista de personal #{index + 1} | {members.length} miembro
                  {members.length !== 1 ? "s" : ""}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: contractName ? "#666" : "#9e9e9e",
                    fontWeight: contractName ? 700 : 500,
                    my: 0.5,
                    textTransform: contractName ? 'uppercase' : 'none',
                    fontStyle: contractName ? 'normal' : 'italic',
                    fontSize: '0.75rem',
                  }}
                >
                  {contractName || "Sin nombre de contratación"}
                </Typography>
                <Typography variant="body2" sx={{ color: "#888", fontSize: '0.8rem', fontWeight: 500 }}>
                  {formatFullSchedule(list)}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 1 }}>
                <FancyTooltip title="Descargar" placement="top">
                  <IconButton
                    size="small"
                    sx={{ border: '1px solid #d32f2f', bgcolor: '#fff', borderRadius: '6px' }}
                    color="primary"
                    onClick={async () => {
                      await handleOpenDownloadPreview(list.id);
                    }}
                  >
                    <DownloadIcon fontSize="small" />
                  </IconButton>
                </FancyTooltip>
                <FancyTooltip title="Editar" placement="top">
                  <IconButton
                    size="small"
                    sx={{ border: '1px solid #d32f2f', bgcolor: '#fff', color: '#FFD700', borderRadius: '6px' }}
                    onClick={() => openEditForm(list)}
                  >
                    <PenIcon fontSize="small" />
                  </IconButton>
                </FancyTooltip>
                <FancyTooltip title="Eliminar" placement="top">
                  <IconButton
                    size="small"
                    sx={{ border: '1px solid #d32f2f', bgcolor: '#fff', borderRadius: '6px' }}
                    color="error"
                    onClick={() => setDeleteListId(list.id)}
                  >
                    <TrashIcon fontSize="small" />
                  </IconButton>
                </FancyTooltip>
              </Box>
            </Box>
          </>
        ) : (
          <>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1, color: "#333" }}>
              Editando Lista de personal #{index + 1}
            </Typography>
            {renderForm(true)}
          </>
        )}
      </Box>
    );
  };

  // ── Side info section ─────────────────────────────────────────────────────
  const renderSideSection = (
    title: string,
    content: string,
    isEditing: boolean,
    onEdit: () => void,
    onCancel: () => void,
    onSave: () => void,
    onChange: (v: string) => void
  ) => (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "rgba(0,0,0,0.1)",
        borderRadius: "16px",
        p: 2.5,
        mb: 3,
        bgcolor: "#E9E1DD",
        position: 'relative'
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Typography variant="subtitle1" fontWeight={700} sx={{ color: "#333", fontSize: '0.9rem' }}>
          {title}
        </Typography>
      </Box>

      {!isEditing ? (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Typography 
            variant="body2" 
            sx={{ 
              mt: 1, 
              color: "#666", 
              whiteSpace: 'pre-wrap', 
              fontSize: '0.8rem',
              lineHeight: 1.6,
              flex: 1
            }}
          >
            {content || "Sin información registrada."}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            onClick={onEdit}
            startIcon={<PenIcon sx={{ fontSize: '1rem !important', color: '#FFD700' }} />}
            sx={{ 
              ml: 2, 
              borderRadius: '20px', 
              textTransform: 'none',
              bgcolor: '#fff',
              px: 3,
              py: 0.8,
              borderColor: '#d32f2f',
              color: '#d32f2f',
              fontWeight: 600,
              '&:hover': { borderColor: '#b71c1c', bgcolor: 'rgba(211, 47, 47, 0.04)' }
            }}
          >
            Editar
          </Button>
        </Box>
      ) : (
        <>
          <TextField
            multiline
            rows={4}
            value={content}
            onChange={(e) => onChange(e.target.value)}
            fullWidth
            size="small"
            variant="outlined"
            placeholder="Ingrese información..."
            sx={inputSx}
          />
          <Box
            sx={{ display: "flex", gap: 1, justifyContent: "flex-end", mt: 1.5 }}
          >
            <Button 
              variant="text" 
              color="inherit" 
              size="small" 
              onClick={onCancel}
              sx={{ textTransform: 'none', borderRadius: '20px' }}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={onSave}
              sx={{ textTransform: 'none', borderRadius: '20px', px: 3 }}
            >
              Guardar cambios
            </Button>
          </Box>
        </>
      )}
    </Box>
  );

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <FancyPaper
        pagetitle={decodedProjectName || "Lista de personal"}
        prevpage="Proyectos"
        linkprevpage="/dashboard/projects"
      >
        <Grid container spacing={4} sx={{ mt: -2 }}>
          {/* Listas registradas */}
          <Grid item xs={12}>
            <Box
              sx={{
                mb: 2,
                pb: 1,
                borderBottom: '2px solid #333'
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.2rem' }}>
                Listas registradas
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
                mt: 3,
                bgcolor: '#E9E1DD',
                p: 2,
                borderRadius: '8px',
                border: '1px solid rgba(0,0,0,0.1)'
              }}
            >
              <Typography variant="body1" sx={{ fontWeight: 600, color: '#333' }}>
                {staffLists.length} lista{staffLists.length !== 1 ? "s" : ""}{" "}
                registrada{staffLists.length !== 1 ? "s" : ""}
              </Typography>
              {!showAddForm && editingListId === null && (
                <Button
                  variant="contained"
                  color="error"
                  onClick={openAddForm}
                  startIcon={<PlusIcon />}
                  sx={{ 
                    borderRadius: '20px', 
                    textTransform: 'none',
                    bgcolor: '#d32f2f',
                    color: '#fff',
                    px: 3,
                    fontWeight: 600,
                    '&:hover': { bgcolor: '#b71c1c' }
                  }}
                >
                  Agregar lista
                </Button>
              )}
            </Box>

            {/* Add form */}
            {showAddForm && renderForm(false)}

            {/* List of existing lists */}
            {staffLists.length === 0 && !showAddForm ? (
              <Box sx={{ bgcolor: '#E9E1DD', p: 3, borderRadius: '16px', textAlign: 'center', border: '1px dashed #ccc' }}>
                <Typography variant="body1" sx={{ color: '#888' }}>
                  No hay listas registradas
                </Typography>
                <Typography variant="caption" sx={{ color: '#aaa' }}>
                  Agregue una nueva lista para que se refleje aquí
                </Typography>
              </Box>
            ) : (
              staffLists.map((list, idx) => renderListCard(list, idx))
            )}
          </Grid>

          {/* Registro de remitente */}
          <Grid item xs={12}>
            <Box sx={{ mb: 2, pb: 1, borderBottom: '2px solid #d32f2f' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#d32f2f' }}>
                Registro de remitente
              </Typography>
            </Box>
            {renderSideSection(
              "",
              senderContent,
              editSender,
              () => setEditSender(true),
              () => {
                setEditSender(false);
                setSenderContent(sender?.content ?? "");
              },
              handleSaveSender,
              setSenderContent
            )}
          </Grid>

          {/* Lista de vehículos registrados */}
          <Grid item xs={12}>
            <Box sx={{ mb: 2, pb: 1, borderBottom: '2px solid #d32f2f' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#d32f2f' }}>
                Lista de vehículos registrados
              </Typography>
            </Box>
            {renderSideSection(
              "",
              vehicleContent,
              editVehicle,
              () => setEditVehicle(true),
              () => {
                setEditVehicle(false);
                setVehicleContent(vehicle?.content ?? "");
              },
              handleSaveVehicle,
              setVehicleContent
            )}
          </Grid>

          {/* Lista de firmas registradas */}
          <Grid item xs={12}>
            <Box sx={{ mb: 2, pb: 1, borderBottom: '2px solid #d32f2f' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#d32f2f' }}>
                Lista de firmas registradas
              </Typography>
            </Box>
            {renderSideSection(
              "",
              signatureContent,
              editSignature,
              () => setEditSignature(true),
              () => {
                setEditSignature(false);
                setSignatureContent(signature?.content ?? "");
              },
              handleSaveSignature,
              setSignatureContent
            )}
          </Grid>
        </Grid>
      </FancyPaper>

      {/* Delete confirmation dialog */}
      <Dialog
        open={!!deleteListId}
        onClose={() => setDeleteListId(null)}
        maxWidth="xs"
        fullWidth
      >
        <DeleteModal
          textChildren={
            staffLists.find((l) => l.id === deleteListId)?.contractName ||
            "esta lista"
          }
          actionButton={
            <FancyDialogActions
              handleAccept={handleConfirmDelete}
              cancelModal={() => setDeleteListId(null)}
              textAcceptButton={"Eliminar"}
            />
          }
        />
      </Dialog>

      {/* Download dialog */}
      <Dialog
        open={!!downloadListId}
        onClose={() => {
          setDownloadListId(null);
          if (pdfBlobUrl) {
            window.URL.revokeObjectURL(pdfBlobUrl);
            setPdfBlobUrl(null);
          }
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            overflow: "hidden",
          },
        }}
      >
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            p: 0,
            minHeight: "500px",
            bgcolor: "#fff",
          }}
        >
          {/* Header style from image */}
          <Box
            sx={{
              width: "100%",
              height: "40px",
              display: "flex",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                flex: 1,
                bgcolor: "#9D1D1D", // Dark red
                position: "relative",
                "&::after": {
                  content: '""',
                  position: "absolute",
                  right: -30,
                  top: 0,
                  width: 60,
                  height: "100%",
                  bgcolor: "#9D1D1D",
                  transform: "skewX(-30deg)",
                },
              }}
            />
            <Box sx={{ flex: 1, bgcolor: "#4A0000" }} /> {/* Brownish red */}
          </Box>

          <Box sx={{ p: 4, width: "100%", display: "flex", flexDirection: "column", alignItems: "center" }}>
            <Box sx={{ alignSelf: "flex-start", mb: 2 }}>
              <img src={mrpintoresLogo} alt="Logo" style={{ width: "150px" }} />
            </Box>

            {/* Document Preview */}
            <Box
              sx={{
                width: "90%",
                height: "400px",
                border: "1px solid #eee",
                borderRadius: "8px",
                overflow: "hidden",
                bgcolor: "#f9f9f9",
                mb: 4,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {pdfBlobUrl ? (
                <iframe
                  src={`${pdfBlobUrl}#toolbar=0&navpanes=0&scrollbar=0`}
                  width="100%"
                  height="100%"
                  style={{ border: "none" }}
                  title="PDF Preview"
                />
              ) : (
                <Typography color="text.secondary">Cargando previsualización...</Typography>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "flex-end", pb: 3, px: 4, gap: 2 }}>
          <Button
            variant="outlined"
            onClick={() => {
              setDownloadListId(null);
              if (pdfBlobUrl) {
                window.URL.revokeObjectURL(pdfBlobUrl);
                setPdfBlobUrl(null);
              }
            }}
            sx={{
              borderRadius: "12px",
              textTransform: "none",
              px: 4,
              borderColor: "#d32f2f",
              color: "#d32f2f",
              fontWeight: 600,
            }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleDownloadWord}
            startIcon={<DownloadIcon />}
            sx={{
              borderRadius: "12px",
              textTransform: "none",
              px: 4,
              bgcolor: "#d32f2f",
              fontWeight: 600,
              "&:hover": { bgcolor: "#b71c1c" },
            }}
          >
            Word
          </Button>
          <Button
            variant="contained"
            onClick={handleDownloadPdf}
            startIcon={<DownloadIcon />}
            sx={{
              borderRadius: "12px",
              textTransform: "none",
              px: 4,
              bgcolor: "#d32f2f",
              fontWeight: 600,
              "&:hover": { bgcolor: "#b71c1c" },
            }}
          >
            PDF
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ManagementStaffList;
